"use client";
import { cn } from "@/utils/cn";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";

const AUTOPLAY_MS = 5000;

const loadImage = (src: string) =>
  new Promise<string>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(src);
    image.onerror = reject;
    image.src = src;
  });

const slideVariants = {
  initial: {
    scale: 0,
    opacity: 0,
    rotateX: 45,
  },
  visible: {
    scale: 1,
    rotateX: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: [0.645, 0.045, 0.355, 1.0],
    },
  },
  upExit: {
    opacity: 1,
    y: "-150%",
    transition: {
      duration: 1,
    },
  },
  downExit: {
    opacity: 1,
    y: "150%",
    transition: {
      duration: 1,
    },
  },
};

export const ImagesSlider = ({
  images,
  children,
  overlay = true,
  overlayClassName,
  className,
  autoplay = true,
  direction = "up",
}: {
  images: string[];
  children: React.ReactNode;
  overlay?: React.ReactNode;
  overlayClassName?: string;
  className?: string;
  autoplay?: boolean;
  direction?: "up" | "down";
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<string[]>([]);
  const loadedCount = useRef(0);

  useEffect(() => {
    loadedCount.current = loadedImages.length;
  }, [loadedImages]);

  // Load the first image on its own so the hero can appear as early as possible, then the
  // rest one at a time. The text on top never waits for any of them.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      for (const src of images) {
        try {
          await loadImage(src);
        } catch {
          console.error(`Failed to load hero image: ${src}`);
          continue;
        }
        if (cancelled) return;
        setLoadedImages((previous) => [...previous, src]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [images]);

  useEffect(() => {
    const step = (delta: number) =>
      setCurrentIndex((index) => {
        const count = Math.max(loadedCount.current, 1);
        return (index + delta + count) % count;
      });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") step(1);
      else if (event.key === "ArrowLeft") step(-1);
    };

    window.addEventListener("keydown", handleKeyDown);
    const interval = autoplay ? window.setInterval(() => step(1), AUTOPLAY_MS) : undefined;

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.clearInterval(interval);
    };
  }, [autoplay]);

  const currentImage = loadedImages.length > 0 ? loadedImages[currentIndex % loadedImages.length] : null;

  return (
    <div
      // Dark base colour so the white text is readable before the first image arrives.
      className={cn(
        "overflow-hidden h-full w-full relative flex items-center justify-center bg-custom-darkblue",
        className
      )}
      style={{
        perspective: "1000px",
      }}
    >
      {children}
      {overlay && (
        <div
          className={cn("absolute inset-0 bg-black/60 z-40", overlayClassName)}
        />
      )}

      {currentImage && (
        <AnimatePresence>
          <motion.img
            key={currentImage}
            src={currentImage}
            // Decorative background: the headline and text carry the meaning.
            alt=""
            aria-hidden="true"
            initial="initial"
            animate="visible"
            exit={direction === "up" ? "upExit" : "downExit"}
            variants={slideVariants}
            className="image h-full w-full absolute inset-0 object-cover object-center"
          />
        </AnimatePresence>
      )}
    </div>
  );
};
